import type { WidgetConfig } from '@digital-cs/shared';

export interface FetchWidgetConfigInput {
  apiBase: string;
  clientId: string;
}

export class WidgetConfigError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | number | null,
    msg: string,
  ) {
    super(msg);
    this.name = 'WidgetConfigError';
  }
}

/**
 * Fetch widget public config from BE.
 *
 * BE 全局 ResponseInterceptor 把响应包成 envelope:
 *   成功: { code: 0, data: <WidgetConfig>, message: 'ok', requestId }
 *   失败: { code: <string|number>, data: null, message: <string>, requestId }
 * 因此 fetcher 必须 unwrap `body.data` 而不是直接 `body`;
 * 错误时也读 envelope 顶层 `code` 作为 WidgetConfigError.code。
 */
export async function fetchWidgetConfig(
  input: FetchWidgetConfigInput,
): Promise<WidgetConfig> {
  const base = input.apiBase.replace(/\/+$/, '');
  const url = `${base}/public/widget/config?clientId=${encodeURIComponent(input.clientId)}`;
  // no-store:config 含 audioEnabled 等会随后端开关变化的运行时值,绝不能用浏览器缓存的旧值
  // (否则 BE 开 TTS 后刷新仍可能拿到 audioEnabled=false,数字人退回 web 式不等音频)
  const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
  const text = await res.text();
  let body: { code?: string | number; data?: unknown; message?: string };
  try {
    body = JSON.parse(text);
  } catch {
    throw new WidgetConfigError(
      res.status,
      null,
      `widget config non-json response: ${text.slice(0, 80)}`,
    );
  }
  if (!res.ok) {
    throw new WidgetConfigError(
      res.status,
      body?.code ?? null,
      `widget config ${res.status}: ${body?.message ?? body?.code ?? 'unknown'}`,
    );
  }
  return body?.data as WidgetConfig;
}
